/**
 * Umsatzsteuer einer Bestellung — abhängig vom Land des Rechnungsempfängers.
 *
 * Anlass (24.10.2026): AUDAX Suisse bestellte Premium und bekam 19 % deutsche MwSt. berechnet.
 * Eine Leistung an einen Kunden außerhalb der EU ist in Deutschland aber nicht steuerbar
 * (§ 3a UStG, Leistungsort beim Empfänger), zu zahlen waren also 69,00 € statt 82,11 €.
 *
 * Die Regel in vier Fällen:
 *
 * | Rechnungsempfänger         | MwSt. | Auf der Rechnung                                     |
 * |----------------------------|-------|------------------------------------------------------|
 * | Deutschland                | 19 %  | wie bisher                                           |
 * | außerhalb der EU           | 0 %   | „Nicht im Inland steuerbare Leistung“                |
 * | EU mit USt-IdNr.           | 0 %   | „Steuerschuldnerschaft des Leistungsempfängers“      |
 * | EU ohne USt-IdNr.          | 19 %  | wie bisher (unter der EU-Schwelle von 10.000 €/Jahr) |
 *
 * **Der Server entscheidet**, beim Anlegen der Bestellung, und friert Fall und Steuersatz ein.
 * Diese Datei ist der Spiegel für die Oberfläche: sie zeigt am Bestellknopf denselben Betrag,
 * den der Server gleich festschreibt. Gegenstück: `model/Tax.kt` im Backend — beide ändern.
 */
import { COUNTRY_LABELS, CountryCode } from './brand';

export type TaxTreatment = 'DOMESTIC' | 'EU_REVERSE_CHARGE' | 'EU_WITHOUT_VAT_ID' | 'NON_EU';

/**
 * EU-Mitglieder außer Deutschland, mit dem Präfix ihrer USt-IdNr.
 *
 * Griechenland schreibt `EL`, nicht `GR`. Monaco gehört umsatzsteuerlich zu Frankreich und
 * vergibt französische Nummern. Spiegel von `EU_VAT_PREFIXES` in `model/Tax.kt`.
 */
export const EU_VAT_PREFIXES: Partial<Record<CountryCode, string>> = {
  AT: 'AT', BE: 'BE', BG: 'BG', CY: 'CY', CZ: 'CZ', DK: 'DK', EE: 'EE', ES: 'ES', FI: 'FI',
  FR: 'FR', GR: 'EL', HR: 'HR', HU: 'HU', IE: 'IE', IT: 'IT', LT: 'LT', LU: 'LU', LV: 'LV',
  MT: 'MT', NL: 'NL', PL: 'PL', PT: 'PT', RO: 'RO', SE: 'SE', SI: 'SI', SK: 'SK',
  MC: 'FR',
};

/** Liegt das Land in der EU (ohne Deutschland)? Nur dort gibt es die Frage nach der USt-IdNr. */
export function isEuForeign(country?: string | null): boolean {
  return !!country && country.toUpperCase() in EU_VAT_PREFIXES;
}

/** USt-IdNr. ohne Leerzeichen, Punkte und Bindestriche, in Großbuchstaben: „ATU 123.456-78“ → „ATU12345678“. */
export function normalizeVatId(vatId?: string | null): string {
  return (vatId ?? '').replace(/[\s.\-]/g, '').toUpperCase();
}

/**
 * Passt die USt-IdNr. zum Land? Nur das Muster — Präfix des Landes, danach 2 bis 12 Zeichen.
 *
 * Bewusst keine Abfrage beim EU-Register (VIES): die Redaktion prüft eine Nummer, wenn eine
 * kommt. Das Muster fängt den häufigen Fehler ab, die eigene Steuernummer einzutragen.
 */
export function vatIdMatchesCountry(country: string | null | undefined, vatId: string | null | undefined): boolean {
  const prefix = country ? EU_VAT_PREFIXES[country.toUpperCase() as CountryCode] : undefined;
  if (!prefix) return false;
  const id = normalizeVatId(vatId);
  return id.startsWith(prefix) && /^[A-Z0-9]{2,12}$/.test(id.slice(prefix.length));
}

/** Der Steuerfall zu Land und USt-IdNr. Ohne Land gilt Deutschland. */
export function taxTreatmentOf(country?: string | null, vatId?: string | null): TaxTreatment {
  const c = (country || 'DE').toUpperCase();
  if (c === 'DE') return 'DOMESTIC';
  if (isEuForeign(c)) return vatIdMatchesCountry(c, vatId) ? 'EU_REVERSE_CHARGE' : 'EU_WITHOUT_VAT_ID';
  return 'NON_EU';
}

/** Wird ohne MwSt. abgerechnet? */
export function isZeroRated(treatment: TaxTreatment): boolean {
  return treatment === 'NON_EU' || treatment === 'EU_REVERSE_CHARGE';
}

/**
 * Was am Betrag steht, wenn keine MwSt. anfällt — für Checkout, Bestätigung, Buchungsliste.
 * `null` heißt: die übliche Angabe „inkl. 19 % MwSt.“.
 */
export const TAX_TREATMENT_LABELS: Record<TaxTreatment, string | null> = {
  DOMESTIC: null,
  EU_WITHOUT_VAT_ID: null,
  NON_EU: 'ohne MwSt. (Leistung ins Ausland)',
  EU_REVERSE_CHARGE: 'ohne MwSt. (Reverse Charge)',
};

/** Die Angabe am zu zahlenden Betrag: „inkl. 19 % MwSt.“ oder der Grund, warum keine anfällt. */
export function payableTaxLabel(treatment: TaxTreatment | undefined, vatPercent: number): string {
  return TAX_TREATMENT_LABELS[treatment ?? 'DOMESTIC'] ?? `inkl. ${vatPercent} % MwSt.`;
}

/**
 * Der zu zahlende Betrag eines Angebots für diesen Steuerfall.
 *
 * Das Angebot (`booking-options`) kennt das Land noch nicht und rechnet mit dem Steuersatz des
 * Preises. Ohne MwSt. ist der zu zahlende Betrag der Nettopreis — das ist keine eigene Rechnung,
 * sondern die Wahl zwischen zwei Zahlen, die der Server geliefert hat.
 */
export function payableCents(option: { netCents: number; grossCents: number }, treatment: TaxTreatment): number {
  return isZeroRated(treatment) ? option.netCents : option.grossCents;
}

/**
 * Rechnungsempfänger einer Bestellung als Textblock — Name, Anschrift, Land (außer Deutschland,
 * und nur, wenn es nicht schon in der Anschrift steht) und die USt-IdNr. So steht es auch auf der
 * Rechnung (`BookingRequestService.recipientAddressOf`).
 */
export function billingText(booking: {
  billingName?: string; billingAddress?: string; billingCountry?: string; vatId?: string;
}): string {
  const lines = [booking.billingName, booking.billingAddress?.trim()].filter(Boolean) as string[];
  const country = booking.billingCountry && booking.billingCountry !== 'DE'
    ? COUNTRY_LABELS[booking.billingCountry as CountryCode] ?? booking.billingCountry
    : null;
  const last = (booking.billingAddress ?? '').trim().split('\n').pop()?.trim().toLowerCase();
  if (country && last !== country.toLowerCase()) lines.push(country);
  if (booking.vatId) lines.push(`USt-IdNr. ${booking.vatId}`);
  return lines.join('\n');
}
